import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Camera, ImagePlus, X, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified Grey Bill Form - Single source of truth for all bill creation/editing
 * Used by: GreyBillsList (standalone), OrderDetails (embedded), BillDetails (edit)
 */
const GreyBillForm = ({ 
  orderId = null,
  initialData = null,
  onSuccess,
  onCancel,
  mode = 'create'
}) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [transportLrFile, setTransportLrFile] = useState(null);
  const [transportLrPreview, setTransportLrPreview] = useState(null);
  const [packingSlipFiles, setPackingSlipFiles] = useState([]);
  const [packingSlipPreviews, setPackingSlipPreviews] = useState([]);
  const transportLrRef = useRef(null);
  const packingSlipRef = useRef(null);
  const hasInitialized = useRef(false);

  const [formData, setFormData] = useState({
    order_id: orderId || '',
    purchase_bill_number: '',
    party_name: '',
    billed_quantity: '',
    billed_date: new Date().toISOString().split('T')[0],
    bill_rate: '',
    amount: '',
    freight_charges: '',
    remarks: ''
  });

  useEffect(() => {
    if (mode === 'create') {
      fetchOrders();
    }
    if (initialData && !hasInitialized.current) {
      hasInitialized.current = true;
      setFormData({
        order_id: orderId || initialData.order_id || '',
        purchase_bill_number: initialData.purchase_bill_number || '',
        party_name: initialData.party_name || '',
        billed_quantity: initialData.billed_quantity || '',
        billed_date: initialData.billed_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        bill_rate: initialData.bill_rate || '',
        amount: initialData.amount || '',
        freight_charges: initialData.freight_charges || '',
        remarks: initialData.remarks || ''
      });
      if (initialData.order_rate) {
        setSelectedOrder({ rate: initialData.order_rate, order_unit: initialData.order_unit, supplier_name: initialData.supplier_name });
      }
      if (initialData.transport_lr_photo) {
        setTransportLrPreview(`${API_URL}${initialData.transport_lr_photo}`);
      }
      if (initialData.packing_slip_photos?.length > 0) {
        setPackingSlipPreviews(initialData.packing_slip_photos.map(p => `${API_URL}${p}`));
      }
    }
  }, [initialData, mode, orderId]);

  useEffect(() => {
    if (orderId && orders.length > 0) {
      const order = orders.find(o => o.order_id === orderId);
      if (order) {
        setSelectedOrder(order);
        if (!formData.party_name) {
          setFormData(prev => ({ ...prev, party_name: order.supplier_name || '' }));
        }
      }
    }
  }, [orderId, orders]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/grey-orders');
      setOrders(response.data);
      if (orderId) {
        const order = response.data.find(o => o.order_id === orderId);
        if (order) {
          setSelectedOrder(order);
          if (!formData.party_name) {
            setFormData(prev => ({ ...prev, party_name: order.supplier_name || '' }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch orders', error);
    }
  };

  const handleOrderChange = (value) => {
    const order = orders.find(o => o.order_id === value);
    setSelectedOrder(order);
    setFormData(prev => ({
      ...prev,
      order_id: value,
      party_name: order?.supplier_name || prev.party_name
    }));
  };

  const handleTransportLrSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTransportLrFile(file);
      setTransportLrPreview(URL.createObjectURL(file));
    }
  };

  const handlePackingSlipSelect = (e) => {
    const files = Array.from(e.target.files);
    setPackingSlipFiles(prev => [...prev, ...files]);
    setPackingSlipPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removePackingSlip = (index) => {
    setPackingSlipFiles(prev => prev.filter((_, i) => i !== index));
    setPackingSlipPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (billId) => {
    const uploads = [];
    if (transportLrFile) {
      const fd = new FormData();
      fd.append('file', transportLrFile);
      fd.append('file_type', 'transport_lr_photo');
      uploads.push(api.post(`/grey-bills/${billId}/upload-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
    }
    for (const file of packingSlipFiles) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('file_type', 'packing_slip');
      uploads.push(api.post(`/grey-bills/${billId}/upload-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
    }
    if (uploads.length > 0) {
      await Promise.all(uploads);
    }
  };

  const orderRate = selectedOrder?.rate || initialData?.order_rate;
  const orderUnit = selectedOrder?.order_unit || initialData?.order_unit;
  const billRate = parseFloat(formData.bill_rate) || 0;
  const hasRateMismatch = formData.bill_rate && orderRate && Math.abs(billRate - orderRate) > 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const effectiveOrderId = formData.order_id || orderId;
    if (!effectiveOrderId || !formData.purchase_bill_number || !formData.party_name || !formData.billed_quantity) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const billedQty = parseFloat(formData.billed_quantity);
    if (!billedQty || billedQty <= 0) {
      toast.error('Billed quantity must be greater than 0');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        order_id: effectiveOrderId,
        purchase_bill_number: formData.purchase_bill_number,
        party_name: formData.party_name,
        billed_quantity: parseFloat(formData.billed_quantity),
        billed_date: formData.billed_date,
        bill_rate: formData.bill_rate ? parseFloat(formData.bill_rate) : null,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        freight_charges: formData.freight_charges ? parseFloat(formData.freight_charges) : 0,
        remarks: formData.remarks || null
      };

      if (mode === 'edit' && initialData?.bill_id) {
        await api.put(`/grey-bills/${initialData.bill_id}`, payload);
        await uploadFiles(initialData.bill_id);
        let message = 'Bill updated successfully!';
        if (hasRateMismatch) message += ' Rate mismatch detected';
        toast.success(message);
      } else {
        const res = await api.post('/grey-bills', payload);
        const newBillId = res.data.bill_id;
        await uploadFiles(newBillId);
        let message = `Bill added successfully! (${formData.billed_quantity} ${orderUnit || 'units'})`;
        if (hasRateMismatch) message += ' Rate mismatch detected';
        toast.success(message);
      }
      
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${mode === 'edit' ? 'update' : 'add'} bill`);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mode === 'create' && !orderId && (
        <div>
          <Label htmlFor="order_id">Order *</Label>
          <Select 
            value={formData.order_id} 
            onValueChange={handleOrderChange}
          >
            <SelectTrigger data-testid="order-select" className="h-11">
              <SelectValue placeholder="Select order" />
            </SelectTrigger>
            <SelectContent>
              {orders.map((order) => (
                <SelectItem key={order.order_id} value={order.order_id}>
                  {order.order_number} - {order.supplier_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {orderId && selectedOrder && (
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-600">Order: <span className="font-mono font-medium text-slate-900">{selectedOrder.order_number}</span></p>
          <p className="text-xs text-slate-500 mt-1">Supplier: {selectedOrder.supplier_name} | Unit: {selectedOrder.order_unit}</p>
        </div>
      )}

      <div>
        <Label htmlFor="purchase_bill_number">Purchase Bill Number *</Label>
        <Input
          id="purchase_bill_number"
          data-testid="bill-number-input"
          value={formData.purchase_bill_number}
          onChange={(e) => setFormData({ ...formData, purchase_bill_number: e.target.value })}
          placeholder="PB-2026-001"
          required
          className="h-11"
        />
      </div>

      <div>
        <Label htmlFor="party_name">Party Name *</Label>
        <Input
          id="party_name"
          data-testid="party-name-input"
          value={formData.party_name}
          onChange={(e) => setFormData({ ...formData, party_name: e.target.value })}
          placeholder="Supplier name"
          required
          className="h-11"
        />
      </div>

      <div>
        <Label htmlFor="billed_quantity">Billed Quantity ({orderUnit || 'units'}) *</Label>
        <Input
          id="billed_quantity"
          data-testid="billed-quantity-input"
          type="number"
          step="0.01"
          value={formData.billed_quantity}
          onChange={(e) => setFormData({ ...formData, billed_quantity: e.target.value })}
          placeholder="1000"
          required
          className="h-11"
        />
      </div>

      <div>
        <Label htmlFor="billed_date">Billed Date *</Label>
        <Input
          id="billed_date"
          data-testid="billed-date-input"
          type="date"
          value={formData.billed_date}
          onChange={(e) => setFormData({ ...formData, billed_date: e.target.value })}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
        <div className="flex justify-between items-center">
          <Label className="text-slate-600">Order Rate (Reference)</Label>
          <span className="font-mono font-medium text-slate-900">{orderRate ? `₹${orderRate} / meter` : '-'}</span>
        </div>
        
        <div>
          <Label htmlFor="bill_rate">Bill Rate (per meter)</Label>
          <Input
            id="bill_rate"
            data-testid="bill-rate-input"
            type="number"
            step="0.01"
            value={formData.bill_rate}
            onChange={(e) => setFormData({ ...formData, bill_rate: e.target.value })}
            placeholder="21.76"
            className={`h-11 ${hasRateMismatch ? 'border-amber-400 bg-amber-50' : ''}`}
          />
          {hasRateMismatch && (
            <div className="flex items-center gap-2 mt-2 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-sm">Bill rate does not match the order rate</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Amount</Label>
          <Input
            id="amount"
            data-testid="bill-amount-input"
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder="50000"
            className="h-11"
          />
        </div>
        <div>
          <Label htmlFor="freight_charges">Freight Charges</Label>
          <Input
            id="freight_charges"
            data-testid="freight-charges-input"
            type="number"
            step="0.01"
            value={formData.freight_charges}
            onChange={(e) => setFormData({ ...formData, freight_charges: e.target.value })}
            placeholder="2000"
            className="h-11"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="remarks">Remarks</Label>
        <Input
          id="remarks"
          data-testid="bill-remarks-input"
          value={formData.remarks}
          onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
          placeholder="Any notes..."
          className="h-11"
        />
      </div>

      {/* Transport LR Photo */}
      <div>
        <Label className="flex items-center gap-1.5 mb-2"><Camera className="h-4 w-4" />Transport LR Photo</Label>
        <input
          ref={transportLrRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleTransportLrSelect}
          data-testid="transport-lr-file-input"
        />
        {transportLrPreview ? (
          <div className="relative group">
            <img
              src={transportLrPreview}
              alt="Transport LR"
              className="w-full h-40 object-cover rounded-lg border border-slate-200"
            />
            <button
              type="button"
              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => { setTransportLrFile(null); setTransportLrPreview(null); }}
            >
              <X className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="absolute bottom-2 right-2 bg-slate-900/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => transportLrRef.current?.click()}
            >
              Replace
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="transport-lr-upload-btn"
            className="w-full h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-slate-400 hover:text-slate-600 transition-colors"
            onClick={() => transportLrRef.current?.click()}
          >
            <Upload className="h-5 w-5" />
            <span className="text-xs">Upload LR Photo</span>
          </button>
        )}
      </div>

      {/* Packing Slip Photos (Multiple) */}
      <div>
        <Label className="flex items-center gap-1.5 mb-2"><ImagePlus className="h-4 w-4" />Packing Slip Photos</Label>
        <input
          ref={packingSlipRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handlePackingSlipSelect}
          data-testid="packing-slip-file-input"
        />
        {packingSlipPreviews.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-2">
            {packingSlipPreviews.map((preview, idx) => (
              <div key={`slip-${idx}-${preview.slice(-10)}`} className="relative group">
                <img
                  src={preview}
                  alt={`Packing slip ${idx + 1}`}
                  className="w-full h-24 object-cover rounded-lg border border-slate-200"
                />
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removePackingSlip(idx)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          data-testid="packing-slip-upload-btn"
          className="w-full h-16 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center gap-2 text-slate-500 hover:border-slate-400 hover:text-slate-600 transition-colors"
          onClick={() => packingSlipRef.current?.click()}
        >
          <ImagePlus className="h-4 w-4" />
          <span className="text-xs">{packingSlipPreviews.length > 0 ? 'Add More Photos' : 'Upload Packing Slip Photos'}</span>
        </button>
      </div>

      <div className="pt-4 flex gap-2">
        <Button
          data-testid="submit-bill-btn"
          type="submit"
          disabled={loading}
          className="flex-1 bg-slate-900 hover:bg-slate-800 h-11"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'edit' ? 'Saving...' : 'Adding...'}
            </>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Add Bill'
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="h-11">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default GreyBillForm;
